/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Observable, of } from 'rxjs';
import { bufferTime, catchError, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { ActionCreators as HistoryActionCreators, TerminalHistoryUpdatePayload } from '../terminalHistory/actions';
import { ActionCreators as SteerableBeamsActionCreators } from '../steerableBeams/actions';
import { pollingTimer, RootState } from '../index';
import Terminal, { TerminalDetails } from '../../shared/entities/terminal';
import { handleErrorInterceptor } from '../../utils/AuthUtils';
import { isPathnameFullSpaceVehicleView, isPathnameFullTailView } from '../../utils/NavigatorUtils';
import isNil from 'lodash/isNil';
import { shouldUseOtherTerminalOnAircraftForUpdate } from '../../utils/TerminalUtilities';
import { PortalsEnum } from '../../shared/entities/portal';

const sseTerminalsSuccess = (response: Terminal[]): any => {
  return ActionCreators.ReceivedTerminalsSuccess.create({ response });
};

const sseTerminalUpdateSuccess = (response: Terminal[]): any => {
  return ActionCreators.ReceivedTerminalUpdateSuccess.create(response);
};

const sseTerminalsError = (message: any): any => {
  return ActionCreators.ReceivedTerminalsError.create({ message });
};

const requestTerminalsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedTerminalsSuccess.create(response);
};

const requestTerminalsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedTerminalsError.create(error);
};

export const getTerminalsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw, sse }: any,
): Observable<Action> => {
  if (state$.value.config.appConfig.useSse) {
    return action$.pipe(
      ofType(ActionCreators.RequestTerminals.type),
      switchMap(
        ($action): Observable<Action> =>
          sse.get('/terminals/sse').pipe(
            bufferTime(2000),
            map((response: any[]): Action => {
              if (response.length > 0 && Array.isArray(response[0])) {
                return sseTerminalsSuccess(response[0]);
              }
              return sseTerminalUpdateSuccess(response);
            }),
            catchError((errorMessage: string): Observable<Action> => of(sseTerminalsError(errorMessage))),
            takeUntil(action$.pipe(ofType(ActionCreators.CancelRequestTerminalsPoll.type))),
          ),
      ),
    );
  }
  return action$.pipe(
    ofType(ActionCreators.RequestTerminals.type),
    pollingTimer(
      action$,
      state$.value.config.appConfig.apiPollingIntervalSeconds,
      ActionCreators.CancelRequestTerminalsPoll.type,
    ),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get('/terminals', $action.payload).pipe(
          map((response: AjaxResponse<any>): Action => requestTerminalsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(requestTerminalsError(error))),
          ),
        ),
    ),
  );
};

// Request the terminals endpoint once without polling
export const getTerminalsOnceEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> => {
  return action$.pipe(
    ofType(ActionCreators.RequestTerminalsOnce.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get('/terminals', $action.payload).pipe(
          map((response: AjaxResponse<any>): Action => requestTerminalsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(requestTerminalsError(error))),
          ),
        ),
    ),
  );
};

const requestTerminalSuccess = (response: AjaxResponse<any>, state: RootState, portal: PortalsEnum): any => {
  const terminalDetails = response.response as TerminalDetails;
  const overlaysContextUiState = state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext];
  return ActionCreators.ReceivedTerminalSuccess.create({
    terminal: terminalDetails,
    portal,
    useOtherTerminalOnPlatformDataForUpdate: shouldUseOtherTerminalOnAircraftForUpdate(
      terminalDetails,
      overlaysContextUiState.selectedDatapathUuidInPlatformList,
      overlaysContextUiState.selectedTerminalTabIndex,
    ),
  });
};

const updateCurrentTerminalMetrics = (response: AjaxResponse<any>, state: RootState) => {
  const terminalDetails = response.response as TerminalDetails;
  const overlaysContextUiState = state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext];
  return HistoryActionCreators.ReceivedTerminalHistoryUpdateSuccess.create({
    terminal: terminalDetails,
    useOtherTerminalOnPlatformDataForUpdate: shouldUseOtherTerminalOnAircraftForUpdate(
      terminalDetails,
      overlaysContextUiState.selectedDatapathUuidInPlatformList,
      overlaysContextUiState.selectedTerminalTabIndex,
    ),
  } as TerminalHistoryUpdatePayload);
};

const updateSteerableBeamMetrics = (response: AjaxResponse<any>) => {
  if (
    !isNil(response.response) &&
    !isNil(response.response.steerableBeamsSummary) &&
    response.response.isUsingSteerableBeam
  ) {
    return SteerableBeamsActionCreators.ReceivedSteerableBeamsSuccess.create({
      response: response.response.steerableBeamsSummary,
    });
  } else {
    // clear out the steerable beam information in the case none is returned from the service
    return SteerableBeamsActionCreators.ReceivedSteerableBeamsSuccess.create({});
  }
};

const requestTerminalError = (error: AjaxError): any => {
  return ActionCreators.ReceivedTailDetailsPanelTerminalError.create(
    error.message !== undefined ? error.message : 'Error requesting tail details',
  );
};

export const getTerminalEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestTailDetailsPanelTerminal.type),
    pollingTimer(
      action$,
      state$.value.config.appConfig.apiPollingIntervalSeconds,
      ActionCreators.CancelRequestTailDetailsPanelTerminal.type,
    ),
    switchMap(($action: any): Observable<Action> => {
      let routerPathname = state$.value.router.location.pathname;
      const pathname = routerPathname.substr(1, routerPathname.length);
      const isFullTailView = isPathnameFullTailView(pathname) || isPathnameFullSpaceVehicleView(pathname);
      let url = isFullTailView
        ? `/terminals/${$action.payload.datapathUuid}?fullTailView=true`
        : `/terminals/${$action.payload.datapathUuid}`;

      let terminalSuccessAction = map(
        (response: AjaxResponse<any>): Action => requestTerminalSuccess(response, state$.value, $action.payload.portal),
      );

      // update the terminalHistory and steerableBeams part of the store with the current metrics on the FTV
      if (isFullTailView) {
        terminalSuccessAction = mergeMap((response: AjaxResponse<any>): Action[] => [
          requestTerminalSuccess(response, state$.value, $action.payload.portal),
          updateCurrentTerminalMetrics(response, state$.value),
          updateSteerableBeamMetrics(response),
        ]);
      }

      return ajaxRaw.get(url).pipe(
        terminalSuccessAction,
        catchError(
          (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(requestTerminalError(error))),
        ),
      );
    }),
  );

const requestFlightPathSuccess = (response: AjaxResponse<any>, portal: PortalsEnum): any => {
  return ActionCreators.ReceivedFlightPathSuccess.create({ response: response, portal });
};

const requestFlightPathError = (error: AjaxError, portal: PortalsEnum): any => {
  return ActionCreators.ReceivedFlightPathError.create({
    error: error.message !== undefined ? error.message : 'Error requesting flight path',
    portal,
  });
};

export const getFlightPathEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestFlightPath.type),
    pollingTimer(
      action$,
      state$.value.config.appConfig.apiPollingIntervalSeconds,
      ActionCreators.CancelRequestFlightPath.type,
    ),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/terminals/${$action.payload.datapathUuid}/flightpath`).pipe(
          map((response: AjaxResponse<any>): Action => requestFlightPathSuccess(response, $action.payload.portal)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestFlightPathError(error, $action.payload.portal))),
          ),
        ),
    ),
  );

export const getFlightPathAsyncEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestFlightPathAsync.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/terminals/${$action.payload.datapathUuid}/flightpath`).pipe(
          map((response: AjaxResponse<any>): Action => requestFlightPathSuccess(response, $action.payload.portal)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(requestFlightPathError(error, $action.payload.portal))),
          ),
        ),
    ),
  );
